import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'cipher_current_bu';
const BUContext = createContext(null);

export const useBU = () => {
  const ctx = useContext(BUContext);
  if (!ctx) throw new Error('useBU must be used within BUProvider');
  return ctx;
};

export const BUProvider = ({ user, children }) => {
  const access = useMemo(() => user?.business_unit_access || ['AFFILIATE'], [user]);
  const multi = access.length > 1;

  const [currentBU, setCurrentBUState] = useState(() => {
    if (typeof window === 'undefined') return null;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && access.includes(stored)) return stored;
    // Single-BU operators auto-resolve immediately.
    if (access.length === 1) return access[0];
    return null;
  });

  const setCurrentBU = useCallback((bu) => {
    if (!access.includes(bu)) return;
    try { localStorage.setItem(STORAGE_KEY, bu); } catch { /* noop */ }
    setCurrentBUState(bu);
  }, [access]);

  const clearBU = useCallback(() => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    setCurrentBUState(null);
  }, []);

  // Reconcile whenever the access list changes (e.g. new login) — auto-select
  // if the persisted BU is no longer valid.
  useEffect(() => {
    if (currentBU && !access.includes(currentBU)) {
      if (access.length === 1) {
        setCurrentBU(access[0]);
      } else {
        clearBU();
      }
    } else if (!currentBU && access.length === 1) {
      setCurrentBU(access[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.join(',')]);

  return (
    <BUContext.Provider
      value={{
        currentBU,
        setCurrentBU,
        clearBU,
        access,
        isMultiBU: multi,
        needsSelection: multi && !currentBU,
      }}
    >
      {children}
    </BUContext.Provider>
  );
};
